import { LEGAL_NOTICE } from '../lib/legal-notice.js';
import { ConsentBanner } from '../components/ConsentBanner.js';
import { SiteFooter } from './SiteFooter.js';
import { SiteHeader } from './SiteHeader.js';
import { Capabilities } from './sections/Capabilities.js';
import { Faq } from './sections/Faq.js';
import { FinalCta } from './sections/FinalCta.js';
import { Hero } from './sections/Hero.js';
import { Integrations } from './sections/Integrations.js';
import { MissedCall } from './sections/MissedCall.js';
import { Overflow } from './sections/Overflow.js';
import { Pricing } from './sections/Pricing.js';
import { RoiExample } from './sections/RoiExample.js';
import { Security } from './sections/Security.js';

/**
 * The public page: a salon's day, on its own paper.
 *
 * It is set in daylight, on the colour of a salon wall, and it shows what a
 * salon already reads: the diary of a Saturday (where the product does its one
 * thing, in the page's one moment of motion), the minute-by-minute of a call,
 * the month's statement, the price board at the counter. Headings are lettered
 * narrow and heavy, as a salon's name is painted on its window, and nothing in
 * a heading is coloured for emphasis. Only the close is dark.
 *
 * Every section after the hero shares one layout (`SiteSection`): the heading
 * in a margin on the left, as the hours are in the diary's, so the page has a
 * single edge to read down.
 *
 * Exactly one `<h1>` on the page, in the hero, with every section carrying an
 * `<h2>`. That is not pedantry: it is how a screen reader user navigates a
 * long page, and it is what a search engine reads as the page's subject.
 */
export function Landing({
  signedIn,
  loginUrl,
  signupUrl,
}: {
  readonly signedIn: boolean;
  readonly loginUrl: string;
  /** Creating an account; `/app` itself for somebody already signed in. */
  readonly signupUrl: string;
}): React.JSX.Element {
  // Somebody signed in has an account already: the dashboard offers to create
  // their establishment if they have none, so that is where "start" leads.
  const start = signedIn ? '/app' : signupUrl;
  return (
    <div className="min-h-dvh bg-wall">
      <a href="#contenu" className="skip-link">
        Aller au contenu
      </a>

      <SiteHeader signedIn={signedIn} loginUrl={loginUrl} startUrl={start} />

      <main id="contenu">
        <Hero startUrl={start} signedIn={signedIn} />
        <MissedCall />
        <RoiExample />
        <Capabilities />
        <Integrations />
        <Overflow />
        <Security />
        <Pricing startUrl={start} />
        <Faq />
        <FinalCta contactEmail={LEGAL_NOTICE.contactEmail} />
      </main>

      <SiteFooter loginUrl={loginUrl} />
      <ConsentBanner />
    </div>
  );
}
